import axios from 'axios';
import camelcaseKeys from "camelcase-keys";

/**
 * API Client Configuration
 * Axios instance with interceptors for authentication and error handling
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8081',
  timeout: 300000, // Increased to 1 minute
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple session expired popups
let isSessionExpiredHandled = false;

/**
 * Request Interceptor
 * Adds authentication token to all requests
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Show session expired modal and redirect to login
 */
const handleSessionExpired = (message) => {
  if (isSessionExpiredHandled) return;
  isSessionExpiredHandled = true;

  // Clear auth data
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // Create and show modal
  const overlay = document.createElement('div');
  overlay.id = 'session-expired-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
  `;

  const modal = document.createElement('div');
  modal.style.cssText = `
    background: white;
    padding: 32px;
    border-radius: 12px;
    text-align: center;
    max-width: 400px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  `;

  const icon = document.createElement('div');
  icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;
  icon.style.marginBottom = '16px';

  const title = document.createElement('h2');
  title.textContent = 'Session Expired';
  title.style.cssText = 'margin: 0 0 12px 0; font-size: 24px; font-weight: 600; color: #1f2937;';

  const messageEl = document.createElement('p');
  messageEl.textContent = message || 'Your session has expired. You will be redirected to the login page.';
  messageEl.style.cssText = 'margin: 0 0 24px 0; color: #6b7280; font-size: 14px;';

  const button = document.createElement('button');
  button.textContent = 'Go to Login';
  button.style.cssText = `
    background: #0d9488;
    color: white;
    border: none;
    padding: 12px 32px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
  `;
  button.onclick = () => {
    window.location.href = '/login';
  };

  modal.appendChild(icon);
  modal.appendChild(title);
  modal.appendChild(messageEl);
  modal.appendChild(button);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Auto redirect after 5 seconds
  setTimeout(() => {
    window.location.href = '/login';
  }, 5000);
};

/**
 * Response Interceptor
 * Handles errors globally (401, 403, 500, etc.)
 */
apiClient.interceptors.response.use(
  (response) =>  {
  if (
        response.config?.responseType === 'blob' ||
        response.data instanceof Blob
      ) {
        return response;
      }
    // Convert snake_case response → camelCase
    return camelcaseKeys(response.data, { deep: true });
  },
  (error) => {
    // Handle unauthorized access (session expired, invalid token, etc.)
    if (error.response?.status === 401) {
      // Check if this is a login request - don't show session expired for login failures
      const isLoginRequest = error.config?.url?.includes('/auth/login') || 
                            error.config?.url?.includes('/login');
      
      // Only show session expired modal if NOT a login request
      if (!isLoginRequest) {
        const errorMessage = error.response?.data?.message;

        // Determine the appropriate message based on error type
        let displayMessage = 'Your session has expired. You will be redirected to the login page.';
        if (errorMessage === 'LOGGED_OUT_FROM_ANOTHER_DEVICE') {
          displayMessage = 'You have been logged out because your account was accessed from another device.';
        } else if (errorMessage === 'INVALID_TOKEN') {
          displayMessage = 'Your session is invalid. Please login again.';
        }

        handleSessionExpired(displayMessage);
      }
      
      return Promise.reject(error);
    }

    // Handle forbidden access
    if (error.response?.status === 403) {
      console.error('Access forbidden');
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('Server error occurred');
    }

    return Promise.reject(error);
  }
);

export default apiClient;